import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useTheme } from 'components/core/theme-provider';
import {
  BillingDetailsForm,
  BillingDetailsFormHandle,
} from 'features/subscription/components/billing-details-form';
import { BillingCycleToggle } from 'features/subscription/components/subscription-plan';
import { primaryButtonClass } from 'features/subscription/constants/button-styles';
import { SEAT_POOLS_QUERY_KEY, useBuySeats } from 'features/subscription/hooks/use-seats';
import { billingCycleLabel, uniquePlanIntervals } from 'features/subscription/utils/billing-cycle';
import { cn, formatDate } from 'lib/utils';
import {
  BILLING_PROFILE_INCOMPLETE,
  defaultSeatInterval,
  formatMinor,
  PAYMENT_METHOD_MISSING,
  PlanSeats,
  seatPriceLabel,
  utilitiesErrorCode,
  withInterval,
} from 'features/subscription/utils/seat-pools';
import { AddCardStep, SeatBlockers, useSeatPrice } from './add-seat-dialog';
import { planUnavailableNote, seatsLabel } from './plan-actions';

type T = (key: string, options?: Record<string, unknown> | string) => string;

/** The lowest a plan can go: the seats in use, and for a plan being bought, its minimum. */
const floorOf = (entry: PlanSeats) => (entry.pool ? Math.max(entry.held, entry.minQuantity) : 0);

const step = (entry: PlanSeats, current: number, by: 1 | -1) => {
  if (by === 1 && current === 0) return Math.max(1, entry.minQuantity);
  if (by === -1 && !entry.pool && current <= entry.minQuantity) return 0;
  return current + by;
};

/** A plan not bought yet starts at its first seat; one already held opens on the seats bought. */
const startQuantity = (entry: PlanSeats) => (entry.pool ? entry.purchased : step(entry, 0, 1));

/** "Starter · 6 of 7 seats in use" for a plan held, "Pro · CHF 29 per seat / month" for one to buy. */
const planOptionLabel = (t: T, entry: PlanSeats) => {
  if (entry.pool) {
    return t('PLAN_SEATS_IN_USE_OPTION', {
      plan: entry.name,
      held: entry.held,
      purchased: entry.purchased,
      defaultValue: '{{plan}} · {{held}} of {{purchased}} seats in use',
    });
  }
  const price = seatPriceLabel(t, entry, true);
  return price ? `${entry.name} · ${price}` : entry.name;
};

/** "−1 seat" or "+2 seats". */
const seatDeltaLabel = (t: T, delta: number) => `${delta > 0 ? '+' : '−'}${seatsLabel(t, Math.abs(delta))}`;

const SeatStepper = ({
  entry,
  quantity,
  onChange,
}: {
  entry: PlanSeats;
  quantity: number;
  onChange: (quantity: number) => void;
}) => {
  const { t } = useTranslation();
  const atMax = !entry.canAddSeats || (entry.maxQuantity != null && quantity >= entry.maxQuantity);
  const stepButtonClass =
    'flex size-11 items-center justify-center text-grey-950 transition-colors hover:bg-grey-50 disabled:cursor-not-allowed disabled:text-grey-300 disabled:hover:bg-transparent dark:text-foreground dark:hover:bg-muted';

  return (
    <div className="flex h-11 w-fit items-center overflow-hidden rounded-xl border border-grey-100 dark:border-border">
      <button
        type="button"
        className={stepButtonClass}
        aria-label={t('ONE_LESS_SEAT', { plan: entry.name, defaultValue: 'One less {{plan}} seat' })}
        disabled={!entry.canAddSeats || quantity <= floorOf(entry)}
        onClick={() => onChange(step(entry, quantity, -1))}
      >
        <Minus className="h-4 w-4" />
      </button>
      <output
        className="flex h-full w-14 items-center justify-center border-x border-grey-100 text-sm font-medium tabular-nums text-grey-950 dark:border-border dark:text-foreground"
        aria-live="polite"
      >
        {quantity}
      </output>
      <button
        type="button"
        className={stepButtonClass}
        aria-label={t('ONE_MORE_SEAT', { plan: entry.name, defaultValue: 'One more {{plan}} seat' })}
        disabled={atMax}
        onClick={() => onChange(step(entry, quantity, 1))}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
};

/** Figma 10827:21160: the seat change, the plan's bill after it, and what is charged now. */
const SeatChangeSummary = ({
  entry,
  quantity,
  dueNowMinor,
  currency,
}: {
  entry: PlanSeats;
  quantity: number;
  dueNowMinor: number;
  currency: string;
}) => {
  const { t } = useTranslation();
  const delta = quantity - entry.purchased;
  // A plan held changes its bill at renewal; a first buy starts it today.
  const fromDate = entry.pool && entry.periodEndUtc ? formatDate(entry.periodEndUtc) : '';
  const unit = entry.unitAmountMinor;
  const interval = entry.interval?.toLowerCase();

  return (
    <div className="rounded-section border border-grey-100 text-sm dark:border-border">
      <div className="flex flex-col gap-2 px-4 py-3.5 leading-body">
        {entry.pool && delta !== 0 && (
          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
            <span className="text-grey-600 dark:text-muted-foreground">
              {delta < 0
                ? t('SEATS_REMOVED_ON', {
                    seats: seatDeltaLabel(t, delta),
                    date: fromDate,
                    defaultValue: '{{seats}}, removed on {{date}}',
                  })
                : t('SEATS_ADDED_NOW', { seats: seatDeltaLabel(t, delta), defaultValue: '{{seats}}, added now' })}
            </span>
            {unit != null && (
              <span className="text-grey-950 dark:text-foreground">
                {`${delta > 0 ? '+' : '−'}${formatMinor(Math.abs(delta) * unit, entry.currencyCode)}`}
              </span>
            )}
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
          <span className="text-grey-600 dark:text-muted-foreground">
            {fromDate
              ? t('PLAN_FROM_DATE', { plan: entry.name, date: fromDate, defaultValue: '{{plan}} from {{date}}' })
              : t('PLAN_FROM_TODAY', { plan: entry.name, defaultValue: '{{plan}} from today' })}
          </span>
          <span className="text-grey-950 dark:text-foreground">
            {seatsLabel(t, quantity)}
            {unit != null &&
              interval &&
              ` · ${formatMinor(quantity * unit, entry.currencyCode)} / ${t(`INTERVAL_${interval.toUpperCase()}`, interval)}`}
          </span>
        </div>
      </div>
      <div className="flex justify-between gap-4 border-t border-grey-100 px-4 py-3 font-medium leading-body text-grey-950 dark:border-border dark:text-foreground">
        <span>{t('DUE_NOW', 'Due now')}</span>
        <span>{formatMinor(dueNowMinor, currency)}</span>
      </div>
    </div>
  );
};

interface BuySeatsDialogProps {
  open: boolean;
  planSeats: PlanSeats[];
  /** A plan to open on, when opened from that plan's card. */
  focusCode?: string;
  /** Billing cycle to start a first buy on, when the card already chose yearly or weekly. */
  focusInterval?: string;
  onClose: () => void;
}

/** Figma 10827:20761 / 10827:21127: change the seats of one plan at a time. */
export const BuySeatsDialog = ({
  open,
  planSeats,
  focusCode,
  focusInterval,
  onClose,
}: BuySeatsDialogProps) => {
  const { t } = useTranslation();
  const isDark = useTheme().resolvedTheme === 'dark';
  const queryClient = useQueryClient();
  const { mutate: buySeats, isPending } = useBuySeats();
  const billingRef = useRef<BillingDetailsFormHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [planCode, setPlanCode] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [billingInterval, setBillingInterval] = useState('month');
  const [cardNeededFor, setCardNeededFor] = useState<string | null>(null);
  const [billingRefused, setBillingRefused] = useState(false);
  // Missing billing details split the dialog in two: the seats, then the details and payment.
  const [dialogStep, setDialogStep] = useState<1 | 2>(1);

  const selectPlan = (code: string, startInterval?: string) => {
    const entry = planSeats.find((item) => item.code === code);
    setPlanCode(code);
    if (!entry) return;
    setQuantity(startQuantity(entry));
    setBillingInterval(startInterval && !entry.pool ? startInterval : defaultSeatInterval(entry));
    setBillingRefused(false);
    setCardNeededFor(null);
  };

  useEffect(() => {
    if (!open) return;
    setDialogStep(1);
    // A plan card opens on its own plan; with a single plan there is nothing to choose.
    const code = focusCode ?? (planSeats.length === 1 ? planSeats[0].code : '');
    if (code) selectPlan(code, focusInterval);
    else setPlanCode('');
    setBillingRefused(false);
    setCardNeededFor(null);
    // Reset only when the dialog opens, so a seat refresh underneath does not undo the admin's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusCode, focusInterval]);

  const selected = planSeats.find((entry) => entry.code === planCode);
  const entry = selected ? withInterval(selected, billingInterval) : null;
  // Counted from the seats bought, so a seat decrease already scheduled is not shown as the total.
  const changed = !!entry && quantity !== entry.purchased;
  const price = useSeatPrice(changed ? entry : null, changed ? quantity : null);
  const currency = price.quote?.currencyCode ?? entry?.currencyCode ?? 'CHF';
  const hasBlockers = !!price.problem || price.blockers.length > 0;
  const needsBilling = billingRefused || price.needsBilling;
  // Missing billing details are filled in on step 2 and saved by the pay button, so they do not hold it.
  const canPay =
    changed && (price.ready || (price.needsBilling && !!price.quote && !price.isLoading && !hasBlockers));
  const isBusy = isPending || isSaving;
  // Once on step 2, stay there even after saving the details stops the price asking for them.
  const isTwoStep = needsBilling || dialogStep === 2;
  const isPaymentStep = isTwoStep && dialogStep === 2;
  const cycles = uniquePlanIntervals(selected?.plan?.prices);
  const delta = entry ? quantity - entry.purchased : 0;

  const apply = () => {
    if (!entry) return;
    buySeats([{ entry, quantity }], {
      onSuccess: onClose,
      onError: (error) => {
        const code = utilitiesErrorCode(error);
        const held = entry.pool?.subscription.subscriptionId;
        if (code === PAYMENT_METHOD_MISSING && held) setCardNeededFor(held);
        if (code === BILLING_PROFILE_INCOMPLETE) {
          setBillingRefused(true);
          setDialogStep(2);
        }
      },
    });
  };

  const handlePay = async () => {
    if (isPaymentStep && needsBilling) {
      setIsSaving(true);
      const isSaved = await billingRef.current?.save();
      setIsSaving(false);
      if (!isSaved) return;
    }
    apply();
  };

  const payLabel = () => {
    if (!entry) return t('CHOOSE_A_PLAN', 'Choose a plan');
    if (price.dueNowMinor > 0) {
      return t('PAY_AMOUNT', { amount: formatMinor(price.dueNowMinor, currency), defaultValue: 'Pay {{amount}}' });
    }
    if (delta < 0) {
      return t('REMOVE_SEATS_ACTION', { seats: seatsLabel(t, -delta), defaultValue: 'Remove {{seats}}' });
    }
    if (delta > 0) return t('ADD_SEATS_ACTION', { seats: seatsLabel(t, delta), defaultValue: 'Add {{seats}}' });
    return t('SAVE', 'Save');
  };

  const priceNote = () => {
    if (!entry || entry.unitAmountMinor == null) return '';
    const cycle = entry.interval ? billingCycleLabel(entry.interval, t).toLowerCase() : '';
    const perSeat = formatMinor(entry.unitAmountMinor, entry.currencyCode);
    if (!cycle) return t('PRICE_PER_SEAT_AMOUNT', { price: perSeat, defaultValue: '{{price}} per seat' });
    return t('SEAT_PRICE_BILLED', { price: perSeat, cycle, defaultValue: '{{price}} per seat, billed {{cycle}}' });
  };

  const seatsNote = () => {
    if (!entry) return '';
    if (!entry.canAddSeats) {
      return (
        planUnavailableNote(t, entry) ||
        t('SEATS_FIXED', {
          seats: seatsLabel(t, entry.held + entry.available),
          defaultValue: 'This plan comes with {{seats}}; more cannot be added.',
        })
      );
    }
    if (!entry.pool) return '';
    if (entry.held === 0) {
      return t('SEATS_YOU_HAVE', { purchased: entry.purchased, defaultValue: 'You have {{purchased}}, none in use.' });
    }
    return t('SEATS_YOU_HAVE_FLOOR', {
      purchased: entry.purchased,
      held: entry.held,
      floor: floorOf(entry),
      defaultValue: "You have {{purchased}}, {{held}} in use. You can't go below {{floor}}: free a seat in Teams first.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isBusy && onClose()}>
      {/* Header and footer stay put; only the body scrolls when the billing form is long. */}
      <DialogContent className="!flex max-h-[90dvh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-dialog p-0 sm:max-w-[560px]">
        <DialogHeader className="space-y-1 px-6 pb-5 pr-14 pt-6 text-left sm:px-7 sm:pt-7">
          {isTwoStep && (
            <p className="mb-1 text-xs font-semibold text-grey-500 dark:text-muted-foreground">
              {t('STEP_OF', { step: dialogStep, total: 2, defaultValue: 'Step {{step}} of {{total}}' })}
            </p>
          )}
          <DialogTitle className="text-lg font-medium tracking-normal text-grey-950 dark:text-foreground">
            {isPaymentStep
              ? t('REVIEW_AND_PAY', 'Review and pay')
              : t('BUY_OR_MANAGE_SEATS', 'Buy or manage seats')}
          </DialogTitle>
          <DialogDescription className="text-sm font-normal leading-body text-grey-500 dark:text-muted-foreground">
            {isPaymentStep
              ? t('BUY_SEATS_PAYMENT_DESCRIPTION', 'Add the billing details once, then pay for the seats.')
              : t('BUY_OR_MANAGE_SEATS_DESCRIPTION', 'One plan at a time. Each seat is for one person.')}
          </DialogDescription>
          {isTwoStep && (
            <div className="flex gap-2 pt-3" aria-hidden="true">
              <span className="h-1 flex-1 rounded-full bg-foreground" />
              <span
                className={cn('h-1 flex-1 rounded-full', isPaymentStep ? 'bg-foreground' : 'bg-muted-foreground/30')}
              />
            </div>
          )}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-6 [scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent] sm:px-7">
          {isPaymentStep && entry && (
            <SeatChangeSummary entry={entry} quantity={quantity} dueNowMinor={price.dueNowMinor} currency={currency} />
          )}

          {!isPaymentStep && (
            <>
              <div className="flex flex-col gap-2">
                <label htmlFor="buy-seats-plan" className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
                  {t('PLAN', 'Plan')}
                </label>
                <Select value={planCode} onValueChange={(code) => selectPlan(code)}>
                  <SelectTrigger
                    id="buy-seats-plan"
                    className="rounded-xl border-grey-100 bg-true-white text-grey-950 shadow-none dark:border-border dark:bg-transparent dark:text-foreground"
                  >
                    <SelectValue placeholder={t('CHOOSE_A_PLAN', 'Choose a plan')} />
                  </SelectTrigger>
                  <SelectContent>
                    {planSeats.map((item) => (
                      <SelectItem key={item.code} value={item.code}>
                        {planOptionLabel(t, item)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {entry && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="flex min-w-0 flex-col gap-2 text-sm">
                      <span className="font-medium leading-body text-grey-950 dark:text-foreground">
                        {t('BILLING', 'Billing')}
                      </span>
                      {!entry.pool && cycles.length > 1 ? (
                        <BillingCycleToggle compact intervals={cycles} selectedInterval={billingInterval} onChange={setBillingInterval} />
                      ) : (
                        <span className="leading-body text-grey-600 sm:pb-3 dark:text-muted-foreground">{priceNote()}</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
                        {t('SEATS_IN_TOTAL', 'Seats in total')}
                      </span>
                      <SeatStepper entry={entry} quantity={quantity} onChange={setQuantity} />
                    </div>
                  </div>
                  {seatsNote() && (
                    <p className="text-xs leading-heading text-grey-500 dark:text-muted-foreground">{seatsNote()}</p>
                  )}
                </div>
              )}

              {changed && price.isLoading && (
                <p className="flex items-center gap-2 text-xs text-grey-500 dark:text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('CHECKING_PRICE', 'Checking the price...')}
                </p>
              )}
              {changed && hasBlockers && <SeatBlockers price={price} />}
              {entry && canPay && (
                <SeatChangeSummary entry={entry} quantity={quantity} dueNowMinor={price.dueNowMinor} currency={currency} />
              )}
            </>
          )}

          {isPaymentStep && (
            <BillingDetailsForm
              ref={billingRef}
              hideActions
              onSaved={() => queryClient.invalidateQueries({ queryKey: [SEAT_POOLS_QUERY_KEY, 'quote'] })}
            />
          )}
          {cardNeededFor && <AddCardStep subscriptionId={cardNeededFor} />}
        </div>

        <DialogFooter className="flex-col-reverse gap-2 px-6 pb-6 sm:flex-row sm:justify-end sm:space-x-0 sm:px-7 sm:pb-7">
          <Button
            variant="ghost"
            className="h-10 w-full rounded-full px-4 text-sm font-medium text-grey-900 hover:bg-grey-50 sm:w-auto dark:text-foreground dark:hover:bg-muted"
            onClick={isPaymentStep ? () => setDialogStep(1) : onClose}
            disabled={isBusy}
          >
            {isPaymentStep ? t('BACK', 'Back') : t('CANCEL', 'Cancel')}
          </Button>
          {isTwoStep && !isPaymentStep ? (
            <Button
              className={cn('h-10 w-full rounded-full px-5 sm:w-auto', primaryButtonClass(isDark))}
              onClick={() => setDialogStep(2)}
              disabled={!canPay}
            >
              {t('CONTINUE_TO_BILLING', 'Continue to billing')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              className={cn('h-10 w-full rounded-full px-5 sm:w-auto', primaryButtonClass(isDark))}
              onClick={handlePay}
              disabled={!canPay || isBusy || !!cardNeededFor}
            >
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {payLabel()}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
